import { useEffect, useRef, useState } from "react";

import { useCurrentStateAndParams } from "@uirouter/react";
import styled from "styled-components";

import { Topics } from "@similarweb/icons";
import { colorsPalettes } from "@similarweb/styles";
import { Combobox, DropdownButton } from "@similarweb/ui-components";
import { Box, Flex } from "@similarweb/ui-components/foundations";

import { translate } from "app/filters/i18nFilter";
import { useLLMTabs } from "app/pages/digital-marketing/generative-ai/llm-tracker/shared/hooks/useLLMTabs";
import { ALL_TOPICS_NAME } from "app/pages/digital-marketing/generative-ai/llm-tracker/shared/LLMConsts";
import { navigateToReport } from "app/pages/digital-marketing/generative-ai/llm-tracker/shared/LLMTrackerUtils";
import type { IAiTrackerCampaign, LLModel } from "app/pages/digital-marketing/generative-ai/llm-tracker/shared/types";
import { allTrackers } from "app/services/track/track";

const TruncatedText = styled.span`
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    min-width: 0;
`;

interface ITopicsFilterProps {
    selectedCampaign: IAiTrackerCampaign;
    isDisabled: boolean;
}

export const TopicsFilter = ({ selectedCampaign, isDisabled }: ITopicsFilterProps) => {
    const {
        params: { topic: queryTopic, model: queryModel },
    } = useCurrentStateAndParams();

    const { currentTab } = useLLMTabs();
    const [isOpen, setIsOpen] = useState<boolean>(false);
    const selectedOptionRef = useRef<HTMLDivElement>(null);
    const [selectedTopic, setSelectedTopic] = useState<string>((queryTopic as string) ?? ALL_TOPICS_NAME);
    const isFilterDisabled = !selectedCampaign || isDisabled;
    const items = [ALL_TOPICS_NAME, ...(selectedCampaign.topics ?? [])];
    const getTopicDisplayName = (topic: string): string =>
        topic === ALL_TOPICS_NAME ? translate("llm.topics-filter.all-topics") : topic;
    const displayedTopicName = getTopicDisplayName(selectedTopic);

    const onTopicSelection = (topic: string): void => {
        setIsOpen(false);

        if (topic === selectedTopic) {
            return;
        }

        setSelectedTopic(topic);
        navigateToReport(selectedCampaign, queryModel as LLModel, currentTab.id, { topic });
        allTrackers.dispatchTrackEvent("Drop Down", "click", `Topic Change/${topic}`);
    };

    useEffect(() => {
        if (queryTopic && queryTopic !== selectedTopic) {
            setSelectedTopic(queryTopic as string);
        }
    }, [queryTopic]);

    useEffect(() => {
        if (isOpen) {
            selectedOptionRef.current?.scrollIntoView({ block: "nearest" });
        }
    }, [isOpen]);

    return (
        <Combobox
            data-automation="topics-filter-dropdown"
            width="200px"
            placement="bottom-start"
            isOpen={isOpen}
            onOpenChange={(open) => setIsOpen(open && !isFilterDisabled)}
        >
            <Combobox.Trigger>
                <Flex width="180px">
                    <Box
                        data-automation="topics-filter-dropdown-button"
                        width="100%"
                    >
                        <DropdownButton
                            cssClass="DropdownButton--filtersBarDropdownButton"
                            disabled={isFilterDisabled}
                            isOpen={isOpen}
                            hasValue
                            height={60}
                        >
                            <Flex
                                overflow="hidden"
                                alignItems="center"
                                margin="8px 0"
                                gap="12px"
                            >
                                <Topics
                                    size="sm"
                                    color={colorsPalettes.midnight[100]}
                                    style={{ flexShrink: 0 }}
                                />
                                <TruncatedText title={displayedTopicName}>{displayedTopicName}</TruncatedText>
                            </Flex>
                        </DropdownButton>
                    </Box>
                </Flex>
            </Combobox.Trigger>
            <Combobox.Content>
                {items.map((topic) => {
                    const topicDisplayName = getTopicDisplayName(topic);
                    const isSelected = topic === selectedTopic;

                    return (
                        <Combobox.Option
                            key={topic}
                            id={topic}
                            textValue={topicDisplayName}
                            isSelected={isSelected}
                            onClick={() => onTopicSelection(topic)}
                        >
                            <Box
                                ref={isSelected ? selectedOptionRef : undefined}
                                margin="8px 0"
                                minWidth="0"
                            >
                                <TruncatedText title={topicDisplayName}>{topicDisplayName}</TruncatedText>
                            </Box>
                        </Combobox.Option>
                    );
                })}
            </Combobox.Content>
        </Combobox>
    );
};
